import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { buildApiUrl } from '../../utils/apiBase.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminTablePageShimmer } from '../common/Shimmer.jsx';
import { IconInfo } from '../common/AdminIcons.jsx';
import { HUB_ROLES, PRESET_LABELS, getRoleCapabilitySummary } from '../../config/adminPermissions.js';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminAllowlistManagement.css';

const FILTER_ALL = 'all';
const FILTER_ACTIVE = 'active';
const FILTER_INACTIVE = 'inactive';

const AdminAllowlistManagement = () => {
  const { requestDestructiveAction } = useDestructiveAction();
  const probe = useScreenProbe('allowlist');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [hubRole, setHubRole] = useState(HUB_ROLES.SUPER_ADMIN);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', displayName: '', isActive: true, hubRole: HUB_ROLES.SUPER_ADMIN });

  const editRoleCapabilities = useMemo(
    () => getRoleCapabilitySummary(editForm.hubRole),
    [editForm.hubRole]
  );

  const load = useCallback(async () => {
    probe.start('load');
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(buildApiUrl('/api/admin/allowlist'), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '取得に失敗しました');
      setEntries(data.entries || []);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [probe]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const activeCount = useMemo(
    () => entries.filter((entry) => entry.isActive).length,
    [entries]
  );
  const inactiveCount = entries.length - activeCount;

  const filteredEntries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return entries.filter((entry) => {
      if (statusFilter === FILTER_ACTIVE && !entry.isActive) return false;
      if (statusFilter === FILTER_INACTIVE && entry.isActive) return false;
      if (!query) return true;
      const haystack = `${entry.email || ''} ${entry.displayName || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [entries, searchQuery, statusFilter]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(buildApiUrl('/api/admin/allowlist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim(),
          displayName: displayName.trim() || email.trim(),
          hubRole,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '追加に失敗しました');
      const addedEmail = email.trim();
      setEmail('');
      setDisplayName('');
      setSuccessMessage(`${addedEmail} を許可リストに追加しました。`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      email: entry.email || '',
      displayName: entry.displayName || entry.email || '',
      isActive: entry.isActive !== false,
      hubRole: entry.hubRole || HUB_ROLES.SUPER_ADMIN,
    });
  };

  const closeEditModal = () => {
    setEditingEntry(null);
    setEditForm({ email: '', displayName: '', isActive: true, hubRole: HUB_ROLES.SUPER_ADMIN });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingEntry?.email || !editForm.email.trim()) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(
        buildApiUrl(`/api/admin/allowlist/${encodeURIComponent(editingEntry.email)}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: editForm.email.trim(),
            displayName: editForm.displayName.trim() || editForm.email.trim(),
            isActive: editForm.isActive,
            hubRole: editForm.hubRole,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '更新に失敗しました');
      setSuccessMessage(`${editForm.email.trim()} の許可情報を更新しました。`);
      closeEditModal();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.email) return;

    const result = await requestDestructiveAction({
      title: '許可リスト削除',
      message: `${entry.email} の Google ログイン許可を完全に削除します。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(
        buildApiUrl(`/api/admin/allowlist/${encodeURIComponent(entry.email)}`),
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: result.reason }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '削除に失敗しました');
      setSuccessMessage(`${entry.email} を許可リストから削除しました。`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AdminTablePageShimmer rows={8} columns={4} />;
  }

  return (
    <div className="allowlist-management">
      <header className="allowlist-header">
        <h1>Google ログイン許可</h1>
        <p className="content-description">
          Admin Hub（synapseadminhub）に Google ログインできるアカウントを管理します。
        </p>
      </header>

      <div className="allowlist-info-banner" role="note">
        <IconInfo size={22} className="allowlist-info-banner__icon" />
        <div>
          <strong>運用上の注意</strong>
          <br />
          ここで許可した Google アカウントのみ Admin Hub にログインできます。
        </div>
      </div>

      <div className="allowlist-stats">
        <div className="allowlist-stat-card stat-active">
          <div className="stat-label">有効</div>
          <div className="stat-number">{activeCount}</div>
        </div>
        <div className="allowlist-stat-card stat-inactive">
          <div className="stat-label">無効</div>
          <div className="stat-number">{inactiveCount}</div>
        </div>
        <div className="allowlist-stat-card">
          <div className="stat-label">合計</div>
          <div className="stat-number">{entries.length}</div>
        </div>
      </div>

      {error && (
        <div className="allowlist-alert allowlist-alert-error" role="alert">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="allowlist-alert allowlist-alert-success" role="status">
          {successMessage}
        </div>
      )}

      <form className="allowlist-add-form card" onSubmit={handleAdd}>
        <h2>許可アカウントを追加</h2>
        <p className="form-hint">Google アカウントのメールアドレスを入力して追加します。</p>
        <div className="allowlist-form-grid">
          <div className="allowlist-field">
            <label htmlFor="allowlist-email">メールアドレス</label>
            <input
              id="allowlist-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={saving}
              autoComplete="email"
            />
          </div>
          <div className="allowlist-field">
            <label htmlFor="allowlist-display-name">
              表示名
              <span className="field-note">任意 — 未入力時はメールアドレスを使用</span>
            </label>
            <input
              id="allowlist-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="山田 太郎"
              disabled={saving}
            />
          </div>
          <div className="allowlist-field">
            <label htmlFor="allowlist-hub-role">権限ロール</label>
            <select
              id="allowlist-hub-role"
              className="admin-select"
              value={hubRole}
              onChange={(e) => setHubRole(e.target.value)}
              disabled={saving}
            >
              {Object.entries(PRESET_LABELS).filter(([k]) => k !== 'custom').map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="allowlist-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '追加中...' : '追加する'}
            </button>
          </div>
        </div>
      </form>

      <div className="allowlist-list-card card">
        <div className="allowlist-list-header">
          <h2>許可済みアカウント</h2>
          <div className="allowlist-toolbar">
            <div className="allowlist-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="メール・表示名で検索..."
                aria-label="許可リストを検索"
              />
            </div>
            <div className="allowlist-filter-tabs" role="tablist" aria-label="状態で絞り込み">
              {[
                { id: FILTER_ALL, label: `すべて (${entries.length})` },
                { id: FILTER_ACTIVE, label: `有効 (${activeCount})` },
                { id: FILTER_INACTIVE, label: `無効 (${inactiveCount})` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === tab.id}
                  className={`allowlist-filter-tab${statusFilter === tab.id ? ' active' : ''}`}
                  onClick={() => setStatusFilter(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="allowlist-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            <p>許可されたアカウントはまだありません。</p>
            <p className="empty-hint">上のフォームから最初のアカウントを追加してください。</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="allowlist-empty">
            <p>条件に一致するアカウントがありません。</p>
            <p className="empty-hint">検索キーワードまたはフィルターを変更してください。</p>
          </div>
        ) : (
          <div className="allowlist-table-wrap">
            <table className="allowlist-table">
              <thead>
                <tr>
                  <th>メール</th>
                  <th>表示名</th>
                  <th>ロール</th>
                  <th>状態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className={entry.isActive ? '' : 'row-inactive'}>
                    <td className="allowlist-email">{entry.email}</td>
                    <td>{entry.displayName || '—'}</td>
                    <td>{PRESET_LABELS[entry.hubRole] || entry.hubRole || '—'}</td>
                    <td>
                      <span className={`allowlist-status ${entry.isActive ? 'active' : 'inactive'}`}>
                        {entry.isActive ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>
                      <div className="allowlist-row-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => openEditModal(entry)}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(entry)}
                          disabled={saving}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingEntry && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div
            className="modal allowlist-edit-modal"
            role="dialog"
            aria-labelledby="allowlist-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="allowlist-edit-title">許可アカウントを編集</h2>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div className="allowlist-field">
                  <label htmlFor="allowlist-edit-email">メールアドレス</label>
                  <input
                    id="allowlist-edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                    disabled={saving}
                    autoComplete="email"
                  />
                </div>
                <div className="allowlist-field">
                  <label htmlFor="allowlist-edit-display-name">表示名</label>
                  <input
                    id="allowlist-edit-display-name"
                    type="text"
                    value={editForm.displayName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, displayName: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <label className="allowlist-checkbox-field">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={saving}
                  />
                  <span>ログインを有効にする</span>
                </label>
                <div className="allowlist-field">
                  <label htmlFor="allowlist-edit-hub-role">権限ロール</label>
                  <select
                    id="allowlist-edit-hub-role"
                    className="admin-select"
                    value={editForm.hubRole}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, hubRole: e.target.value }))}
                    disabled={saving}
                  >
                    {Object.entries(PRESET_LABELS).filter(([k]) => k !== 'custom').map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <div className="allowlist-role-capabilities" aria-live="polite">
                    <div className="allowlist-role-capabilities__section">
                      <span className="allowlist-role-capabilities__heading">許可される操作</span>
                      <ul>
                        {editRoleCapabilities.allowed.map((label) => (
                          <li key={`allowed-${label}`}>{label}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="allowlist-role-capabilities__section">
                      <span className="allowlist-role-capabilities__heading">制限される操作</span>
                      {editRoleCapabilities.restricted.length === 0 ? (
                        <p className="allowlist-role-capabilities__none">なし</p>
                      ) : (
                        <ul>
                          {editRoleCapabilities.restricted.map((label) => (
                            <li key={`restricted-${label}`}>{label}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEditModal} disabled={saving}>
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '保存中...' : '保存する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAllowlistManagement;
